import dayjs from '@troon/dayjs';
import { HttpStatusCode } from '@solidjs/start';
import { Button, Container, Heading, HorizontalRule, Link, Page, Pagination, Section, Hero } from '@troon/ui';
import { createAsync, useParams } from '@solidjs/router';
import { For, Show, Suspense } from 'solid-js';
import { Link as HeadLink, Meta, Title } from '@solidjs/meta';
import { IconArrowLeftMd } from '@troon/icons/arrow-left-md';
import { createContentfulListRequest } from '../../../(api)/content/[model]/_client';
import { EventArticle } from '../../../../components/event-article';
import type { RouteDefinition } from '@solidjs/router';

const perPage = 10;

export default function EventsPaginated() {
	const params = useParams<{ page?: string }>();
	const events = createAsync(
		async () => {
			const page = parseInt(params.page ?? '1', 10);
			const today = dayjs().format('YYYY-MM-DD');
			const [upcoming, past] = await Promise.all([
				page === 1
					? getEvents({
							order: ['fields.startDate'],
							'fields.endDate[gt]': today,
						})
					: { items: [] },
				getEvents({
					skip: (page - 1) * perPage,
					limit: perPage,
					'fields.endDate[lte]': today,
				}),
			]);

			return { upcoming: upcoming.items, past: past.items, total: past.total };
		},
		{ deferStream: true },
	);

	return (
		<>
			<Title>
				Access Events<Show when={params.page}>{(page) => ` | Page ${page()}`}</Show> | Exclusive Golf Tournaments,
				Member Experiences & More | Golf with Access
			</Title>
			<Meta
				name="description"
				content="Compete, connect, and celebrate the game. Explore exclusive Access member tournaments, golf events, and experiences at premier courses."
			/>
			<Hero src="https://images.ctfassets.net/rdsy7xf5c8dt/2enNmWsx0CleARWVB0ztxU/ed0a2d16fce8ddf161281ac585f9f412/b4d10513cbac9ecc14d989cae332f479.jpg">
				<Heading as="h1">Events</Heading>
			</Hero>
			<Show when={params.page === '1'}>
				<HeadLink rel="canonical" href="/events" />
			</Show>
			<Container>
				<Page>
					<Suspense>
						<Show when={events.latest?.upcoming.length ? events.latest?.upcoming : null}>
							{(events) => (
								<Section>
									<Heading as="h2" size="h3">
										Upcoming Events
									</Heading>
									<div class="flex flex-col divide-y divide-neutral">
										<For
											each={events()}
											fallback={
												<div class="flex flex-col gap-8">
													<HttpStatusCode code={404} />
													<p>No events found.</p>
													<Button class="w-fit" as={Link} href="/events">
														<IconArrowLeftMd />
														Back to Events
													</Button>
												</div>
											}
										>
											{(event, i) => <EventArticle event={event} eager={i() < 6} preload={i() < 3} />}
										</For>
									</div>
								</Section>
							)}
						</Show>
					</Suspense>

					<Suspense>
						<Show when={events.latest?.past.length ? events.latest?.past : null}>
							{(events) => (
								<Section>
									<Heading as="h2" size="h3">
										Past Events
									</Heading>
									<div class="flex flex-col divide-y divide-neutral">
										<For
											each={events()}
											fallback={
												<div class="flex flex-col gap-8">
													<HttpStatusCode code={404} />
													<p>No events found.</p>
													<Button class="w-fit" as={Link} href="/events">
														<IconArrowLeftMd />
														Back to Events
													</Button>
												</div>
											}
										>
											{(event, i) => <EventArticle event={event} eager={i() < 6} preload={i() < 3} />}
										</For>
									</div>
								</Section>
							)}
						</Show>
					</Suspense>

					<Suspense>
						<Show when={events.latest}>
							{(data) => (
								<>
									<HorizontalRule />
									<Pagination
										currentPage={parseInt(params.page ?? '1', 10)}
										lastPage={Math.ceil(data()!.total / perPage)}
										url={(page) => `/events/page/${page}`}
										nextTitle="Older"
										prevTitle="Newer"
									/>
								</>
							)}
						</Show>
					</Suspense>
				</Page>
			</Container>
		</>
	);
}

const getEvents = createContentfulListRequest('events');

export const route = { info: { nav: { hero: true } } } satisfies RouteDefinition;
